import * as Types from '@aeppic/types'
import { SingleQueryStats } from 'model/indexing/query-stats.js'
import { DocumentIdIterator } from '../../indexing/index-interface'
import BTreeModule from 'sorted-btree'
import type {
  DocumentMatchIterator,
  DocumentMatchIteratorJoined,
  DocumentMatchIteratorUsingId,
  DocumentMatchIteratorUsingIndex,
  KMergedIntersectionIterator,
  KMergedUnionIterator
} from '../iterator-types.js'
import type { EnumerationStrategy } from './strategy.js'
import {
  KMergedIntersection,
  KMergedUnion,
  IterateAllDocuments,
  IterateNoDocuments,
  IterateOverIndex,
  describeIterator,
  isIteratorOverAllDocuments,
  isIteratorOverNoDocuments,
  isIteratorUsingId,
  isIteratorUsingIndexOfDocuments,
  isIteratorUsingJoinsOfIterators
} from '../iterator-types.js'

const BTree: typeof BTreeModule = (BTreeModule as any).default ?? BTreeModule

/**
 * A generic MinHeap implementation.
 * The heap stores elements of type T and orders them based on a provided comparison function.
 */
class MinHeap<T> {
  private heap: T[] = []
  private compareFn: (a: T, b: T) => number

  /**
   * Creates a new MinHeap.
   * @param compareFn A function that compares two elements (a, b) of type T. 
   *                  It should return a negative number if a < b, zero if a === b, and a positive number if a > b.
   */
  constructor(compareFn: (a: T, b: T) => number) {
    this.compareFn = compareFn
  }

  /**
   * Adds an item to the heap.
   * @param item The item to add.
   */
  public push(item: T): void {
    this.heap.push(item)
    this.siftUp()
  }

  /**
   * Removes and returns the smallest item from the heap.
   * @returns The smallest item, or undefined if the heap is empty.
   */
  public pop(): T | undefined {
    if (this.isEmpty()) return undefined
    const item = this.heap[0]
    const last = this.heap.pop()
    if (last !== undefined && !this.isEmpty()) {
      this.heap[0] = last
      this.siftDown()
    }
    return item
  }

  /**
   * Returns the smallest item from the heap without removing it.
   * @returns The smallest item, or undefined if the heap is empty.
   */
  public peek(): T | undefined {
    return this.heap[0]
  }

  /**
   * Checks if the heap is empty.
   * @returns True if the heap is empty, false otherwise.
   */
  public isEmpty(): boolean {
    return this.heap.length === 0
  }

  /**
   * Returns the number of items in the heap.
   * @returns The size of the heap.
   */
  public size(): number {
    return this.heap.length
  }

  private siftUp(): void {
    let nodeIndex = this.heap.length - 1
    while (nodeIndex > 0) {
      const parentIndex = Math.floor((nodeIndex - 1) / 2)
      if (this.compareFn(this.heap[nodeIndex], this.heap[parentIndex]) < 0) {
        [this.heap[nodeIndex], this.heap[parentIndex]] = [this.heap[parentIndex], this.heap[nodeIndex]]
        nodeIndex = parentIndex
      } else {
        break
      }
    }
  }

  private siftDown(): void {
    let nodeIndex = 0
    while (true) {
      const leftChildIndex = 2 * nodeIndex + 1
      const rightChildIndex = 2 * nodeIndex + 2
      let smallestChildIndex = nodeIndex

      if (leftChildIndex < this.heap.length && this.compareFn(this.heap[leftChildIndex], this.heap[smallestChildIndex]) < 0) {
        smallestChildIndex = leftChildIndex
      }
      if (rightChildIndex < this.heap.length && this.compareFn(this.heap[rightChildIndex], this.heap[smallestChildIndex]) < 0) {
        smallestChildIndex = rightChildIndex
      }

      if (smallestChildIndex !== nodeIndex) {
        [this.heap[nodeIndex], this.heap[smallestChildIndex]] = [this.heap[smallestChildIndex], this.heap[nodeIndex]]
        nodeIndex = smallestChildIndex
      } else {
        break
      }
    }
  }
}

export const legacyEnumerationStrategy: EnumerationStrategy = {
  getDocumentIdGenerator,
  enumerateKMergedUnion,
  enumerateKMergedIntersection,
  enumerateAllDocumentsInIndexIterator,
  iteratorContains,
  enumerateGraphIterator,
  enumerateIntersectionOfIterators,
  enumerateUnionOfIterators,
  _enumerateBTreeIntersection
}


/**
 * Represents an element stored in the MinHeap during a K-Way Union operation.
 * It's a tuple containing:
 *  - `documentId`: The current DocumentId from the sub-iterator.
 *  - `iterator`: The sub-iterator (generator) itself.
 *  - `iteratorIndex`: The original index of the sub-iterator (for debugging or tracking purposes).
 */
type KWayUnionHeapElement = [
  documentId: Types.DocumentId,
  iterator: Generator<Types.DocumentId, void, unknown>,
  iteratorIndex: number
];

/**
 * Creates a generator that yields sorted DocumentIds from a given DocumentMatchIterator.
 * This function is crucial for providing sorted streams to k-way merge algorithms.
 * It handles various iterator types, including those based on B-Trees, Sets, and recursively
 * calls k-merge enumeration functions if it encounters KMergedUnion or KMergedIntersection iterators.
 *
 * @param iterator The DocumentMatchIterator to process.
 * @param allDocuments Optional map of all documents, used by IterateAllDocuments to get a sorted list of all IDs.
 * @yields {Types.DocumentId} DocumentIds in sorted order.
 * @throws Error if an unsupported iterator type is encountered or if an old IterateOverSubIterators is found.
 */
export function* getDocumentIdGenerator(iterator: DocumentMatchIterator, allDocuments?: Map<string, Types.Document>): Generator<Types.DocumentId, void, unknown> {
  if (isIteratorUsingIndexOfDocuments(iterator)) {
    // DocumentIdIterator already provides sorted iteration
    for (const docId of iterator.idIterator) {
      yield docId
    }
  } else if (isIteratorUsingId(iterator)) {
    yield iterator.id
  } else if (iterator.type === IterateNoDocuments) {
    // Yield nothing
  } else if (iterator.type === KMergedUnion) {
    yield* enumerateKMergedUnion(iterator as KMergedUnionIterator, allDocuments)
  } else if (iterator.type === KMergedIntersection) {
    yield* enumerateKMergedIntersection(iterator as KMergedIntersectionIterator, allDocuments)
  } else if (iterator.type === IterateAllDocuments && allDocuments) {
    const sortedIds = [...allDocuments.keys()].sort()
    for (const id of sortedIds) {
      yield id
    }
  } else if (isIteratorUsingJoinsOfIterators(iterator)) {
    // Handle old IterateOverSubIterators by recursively processing sub-iterators
    if (iterator.operator === 'union') {
      // For union: yield all documents from all sub-iterators (with deduplication handled by caller)
      for (const subIterator of iterator.iterators) {
        yield* getDocumentIdGenerator(subIterator, allDocuments)
      }
    } else if (iterator.operator === 'intersect') {
      // For intersection: this is more complex and expensive without sorting
      // Fall back to collecting all IDs and computing intersection manually
      const subIteratorIds: Set<Types.DocumentId>[] = []
      for (const subIterator of iterator.iterators) {
        const ids = new Set<Types.DocumentId>()
        for (const docId of getDocumentIdGenerator(subIterator, allDocuments)) {
          ids.add(docId)
        }
        subIteratorIds.push(ids)
      }
      
      if (subIteratorIds.length === 0) return
      
      // Find intersection of all sets
      let intersection = subIteratorIds[0]
      for (let i = 1; i < subIteratorIds.length; i++) {
        const newIntersection = new Set<Types.DocumentId>()
        for (const docId of intersection) {
          if (subIteratorIds[i].has(docId)) {
            newIntersection.add(docId)
          }
        }
        intersection = newIntersection
      }
      
      // Yield intersection in sorted order
      const sortedIds = [...intersection].sort()
      for (const docId of sortedIds) {
        yield docId
      }
    } else {
      throw new Error(`Unsupported operator for old IterateOverSubIterators: ${iterator.operator}`)
    }
  } else {
    // Consider other iterator types or throw an error for unsupported ones
    // For IterateAllDocuments without allDocuments, it's problematic.
    throw new Error(`Unsupported iterator type for getDocumentIdGenerator: ${String(iterator.type)}`)
  }
}

/**
 * Performs a K-Way Merge Union of DocumentIds from multiple sub-iterators.
 * It expects that `getDocumentIdGenerator` can provide sorted DocumentIds from each sub-iterator.
 * Uses a MinHeap to efficiently find the next smallest unique DocumentId across all sub-iterators.
 *
 * @param iterator The KMergedUnionIterator containing the sub-iterators to merge.
 * @param allDocuments Optional map of all documents, passed down to `getDocumentIdGenerator`.
 * @yields {Types.DocumentId} Unique DocumentIds in sorted order from the union of sub-iterators.
 */
export function* enumerateKMergedUnion(iterator: KMergedUnionIterator, allDocuments?: Map<string, Types.Document>, stats?: SingleQueryStats): Generator<Types.DocumentId, void, unknown> {
  if (iterator.iterators.length === 0) return
  if (iterator.iterators.length === 1) {
    yield* getDocumentIdGenerator(iterator.iterators[0], allDocuments)
    return
  }

  const heap = new MinHeap<KWayUnionHeapElement>((a, b) => {
    if (a[0] < b[0]) return -1;
    if (a[0] > b[0]) return 1;
    return 0;
  });

  // Initialize the heap with the first element from each sub-iterator
  for (let i = 0; i < iterator.iterators.length; i++) {
    const subIteratorGenerator = getDocumentIdGenerator(iterator.iterators[i], allDocuments);
    const firstElement = subIteratorGenerator.next();
    if (!firstElement.done) {
      heap.push([firstElement.value as string, subIteratorGenerator, i]);
    }
  }

  let lastYieldedDocumentId: Types.DocumentId | null = null;
  let heapOperations = 0;
  let documentsYielded = 0;

  while (!heap.isEmpty()) {
    heapOperations++;
    const heapTopElement = heap.pop()!; // [documentId, subIteratorGenerator, originalIteratorIndex]
    const currentDocumentId = heapTopElement[0];
    const currentSubGenerator = heapTopElement[1];
    const originalIteratorIndex = heapTopElement[2];

    if (currentDocumentId !== lastYieldedDocumentId) {
      documentsYielded++;
      yield currentDocumentId;
      lastYieldedDocumentId = currentDocumentId;
    }

    // Get the next element from the sub-iterator that provided the top element
    const nextElement = currentSubGenerator.next();
    if (!nextElement.done) {
      heapOperations++;
      heap.push([nextElement.value as string, currentSubGenerator, originalIteratorIndex]);
    }
  }

  stats?.mark('k-merge-union-stats', {
    heapOperations,
    documentsYielded,
    opsPerDocument: documentsYielded > 0 ? heapOperations / documentsYielded : 0,
    iteratorCount: iterator.iterators.length
  });
}

/**
 * Performs a K-Way Merge Intersection of DocumentIds from multiple sub-iterators.
 * It expects that `getDocumentIdGenerator` can provide sorted DocumentIds from each sub-iterator.
 * Uses a multi-pointer strategy: advances iterators whose current DocumentId is less than the
 * maximum current DocumentId across all iterators until all point to the same ID (which is then yielded),
 * or an iterator is exhausted.
 *
 * @param iterator The KMergedIntersectionIterator containing the sub-iterators to intersect.
 * @param allDocuments Optional map of all documents, passed down to `getDocumentIdGenerator`.
 * @yields {Types.DocumentId} DocumentIds in sorted order that are common to all sub-iterators.
 */
export function* enumerateKMergedIntersection(iterator: KMergedIntersectionIterator, allDocuments?: Map<string, Types.Document>, stats?: SingleQueryStats): Generator<Types.DocumentId, void, unknown> {
  if (iterator.iterators.length === 0) return
  // Optimization: if any sub-iterator is IterateNoDocuments, intersection is empty
  if (iterator.iterators.some(it => it.type === IterateNoDocuments)) return

  if (iterator.iterators.length === 1) {
    yield* getDocumentIdGenerator(iterator.iterators[0], allDocuments)
    return
  }

  const subIteratorGenerators: Generator<Types.DocumentId, void, unknown>[] = [];
  const currentDocumentIdsFromIterators: (Types.DocumentId | null)[] = []; // Stores the current DocumentId from each sub-iterator

  // Initialize generators and populate currentDocumentIdsFromIterators with the first ID from each
  for (const subIterator of iterator.iterators) {
    const docIdGenerator = getDocumentIdGenerator(subIterator, allDocuments);
    subIteratorGenerators.push(docIdGenerator);
    const firstElement = docIdGenerator.next();
    if (firstElement.done) {
      // If any sub-iterator is empty, the intersection is empty.
      return;
    }
    currentDocumentIdsFromIterators.push(firstElement.value as string);
  }

  let iteratorAdvances = 0;
  let comparisonOperations = 0;
  let documentsYielded = 0;

  mainLoop: while (true) {
    let allDocumentIdsAreIdentical = true;
    let maximumDocumentId = currentDocumentIdsFromIterators[0]!;

    // Check if all current document IDs are the same and find the maximum ID
    for (let i = 0; i < currentDocumentIdsFromIterators.length; i++) {
      const currentId = currentDocumentIdsFromIterators[i];
      if (currentId === null) return; // Should ideally not happen if logic is correct

      comparisonOperations++;
      if (i > 0 && currentId !== currentDocumentIdsFromIterators[0]) {
        allDocumentIdsAreIdentical = false;
      }
      if (currentId > maximumDocumentId) {
        maximumDocumentId = currentId;
      }
    }

    if (allDocumentIdsAreIdentical) {
      // All iterators point to the same DocumentId, so yield it.
      documentsYielded++;
      yield currentDocumentIdsFromIterators[0]!;

      // Advance all iterators to their next DocumentId.
      for (let i = 0; i < subIteratorGenerators.length; i++) {
        iteratorAdvances++;
        const nextElement = subIteratorGenerators[i].next();
        if (nextElement.done) {
          // If any iterator is exhausted, the intersection is complete.
          stats?.mark('k-merge-intersection-stats', {
            iteratorAdvances,
            comparisonOperations,
            documentsYielded,
            advancesPerDocument: documentsYielded > 0 ? iteratorAdvances / documentsYielded : 0,
            comparisonsPerDocument: documentsYielded > 0 ? comparisonOperations / documentsYielded : 0,
            iteratorCount: iterator.iterators.length
          });
          return;
        }
        currentDocumentIdsFromIterators[i] = nextElement.value as string;
      }
    } else {
      // Not all DocumentIds are the same. Advance iterators whose current DocumentId is less than maximumDocumentId.
      for (let i = 0; i < subIteratorGenerators.length; i++) {
        while (currentDocumentIdsFromIterators[i]! < maximumDocumentId) {
          iteratorAdvances++;
          const nextElement = subIteratorGenerators[i].next();
          if (nextElement.done) {
            // If any iterator is exhausted while trying to catch up, the intersection is complete.
            stats?.mark('k-merge-intersection-stats', {
              iteratorAdvances,
              comparisonOperations,
              documentsYielded,
              advancesPerDocument: documentsYielded > 0 ? iteratorAdvances / documentsYielded : 0,
              comparisonsPerDocument: documentsYielded > 0 ? comparisonOperations / documentsYielded : 0,
              iteratorCount: iterator.iterators.length
            });
            return;
          }
          currentDocumentIdsFromIterators[i] = nextElement.value as string;
        }
        // If an iterator, after advancing, has a DocumentId greater than the current maximumDocumentId,
        // it means the previous maximumDocumentId was not a common value. 
        // The loop will continue, and a new maximumDocumentId will be determined in the next iteration.
      }
    }
  }
}
export function *enumerateAllDocumentsInIndexIterator(iterator: DocumentMatchIteratorUsingIndex) {
  if (iterator.type === IterateOverIndex) {
    // Iterate directly over the DocumentIdIterator
    for (const documentId of iterator.idIterator) {
      yield documentId
    }
  } else if (isIteratorUsingJoinsOfIterators(iterator)) {
    for (const subIterator of iterator.iterators) {
      yield *enumerateAllDocumentsInIndexIterator(subIterator as DocumentMatchIteratorUsingIndex)
    }
  } else {
    throw new Error('Not supported')
  }
}

export function iteratorContains(iterator: DocumentMatchIteratorUsingIndex, documentId: Types.DocumentId): boolean {
  return iterator.idIterator.has(documentId)
}

export function* enumerateGraphIterator(iterator: DocumentMatchIterator, allDocuments: Map<string, Types.Document>, stats?: SingleQueryStats): IterableIterator<Types.Document> {
  const documentsAlreadyReturned = new Set<string>()

  const info = {
    enumerated: 0,
    duplicates: 0,
  }

  stats?.phase('enumerate-graph', { iterator: describeIterator(iterator), info })

  const ops = stats?.newOpStats('enumerate-graph:yield')

  for (const document of _enumerateGraphIterator(iterator, allDocuments, stats)) {
    info.enumerated++
    const id = document.id

    ops?.beginOp()

    if (!documentsAlreadyReturned.has(id)) {
      documentsAlreadyReturned.add(id)
      yield document
    } else {
      info.duplicates++
    }

    ops?.endOp()
  }
}

function* _enumerateGraphIterator(
  iterator: DocumentMatchIterator,
  allDocuments: Map<string, Types.Document>,
  stats?: SingleQueryStats
): IterableIterator<Types.Document> {
  if (isIteratorOverNoDocuments(iterator)) {
    return;
  } else if (isIteratorUsingId(iterator)) {
    const document = allDocuments.get(iterator.id);
    if (document) yield document;
  } else if (isIteratorUsingIndexOfDocuments(iterator)) {
    // Iterate directly over the DocumentIdIterator
    for (const documentId of iterator.idIterator) {
      const document = allDocuments.get(documentId);
      if (document) yield document;
    }
  } else if (iterator.type === KMergedUnion) {
    // Use the efficient k-merge union implementation
    for (const documentId of enumerateKMergedUnion(iterator as KMergedUnionIterator, allDocuments, stats)) {
      const document = allDocuments.get(documentId);
      if (document) {
        stats?.increment('enumerated:union:k-merge');
        yield document;
      }
    }
  } else if (iterator.type === KMergedIntersection) {
    // Use the efficient k-merge intersection implementation
    for (const documentId of enumerateKMergedIntersection(iterator as KMergedIntersectionIterator, allDocuments, stats)) {
      const document = allDocuments.get(documentId);
      if (document) {
        stats?.increment('enumerated:intersection:k-merge');
        yield document;
      }
    }
  } else if (isIteratorUsingJoinsOfIterators(iterator)) {
    if (iterator.iterators.length === 1) {
      yield* _enumerateGraphIterator(iterator.iterators[0], allDocuments, stats);
    } else if (iterator.operator === 'intersect' && iterator.isSortable) {
      yield* _enumerateSortedIntersection(iterator.iterators, allDocuments, stats);
    } else if (iterator.operator === 'union' && iterator.isSortable) {
      yield* _enumerateSortedUnion(iterator.iterators, allDocuments, stats);
    } else if (iterator.operator === 'intersect') {
      yield* enumerateIntersectionOfIterators(iterator.iterators, allDocuments, stats);
    } else if (iterator.operator === 'union') {
      yield* enumerateUnionOfIterators(iterator.iterators, allDocuments, stats);
    }
  } else if (isIteratorOverAllDocuments(iterator)) {
    yield* allDocuments.values();
  } else {
    throw new Error('Unsupported iterator');
  }
}


function* _enumerateSortedUnion(
  iterators: DocumentMatchIterator[],
  allDocuments: Map<string, Types.Document>,
  stats?: SingleQueryStats
): IterableIterator<Types.Document> {
  // Initialize cursors
  type Cursor = { iter: IterableIterator<Types.Document>; value: Types.Document | undefined };
  const cursors: Cursor[] = iterators
    .map(it => {
      const iter = _enumerateGraphIterator(it, allDocuments, stats);
      const next = iter.next();
      return { iter, value: next.done ? undefined : next.value };
    })
    .filter(c => c.value);

  if (cursors.length === 0) return;

  while (cursors.length > 0) {
    // Find minimum ID
    let vmin = cursors[0].value!.id;
    for (const c of cursors) {
      if (c.value!.id < vmin) vmin = c.value!.id;
    }

    // Yield the document
    stats?.increment('enumerated:union:sorted')
    yield cursors.find(c => c.value!.id === vmin)!.value!;

    // Advance cursors at vmin
    for (let i = 0; i < cursors.length; ) {
      if (cursors[i].value!.id === vmin) {
        const next = cursors[i].iter.next();
        cursors[i].value = next.done ? undefined : next.value;
        if (!cursors[i].value) cursors.splice(i, 1);
        else i++;
      } else {
        i++;
      }
    }
  }
}

export function* enumerateIntersectionOfIterators(iterators: DocumentMatchIterator[], allDocuments: Map<string, Types.Document>, stats?: SingleQueryStats): IterableIterator<Types.Document> {
  if (iterators.length <= 1) {
    throw new Error('Unsupported number of iterators to intersect')
  }

  if (iterators.some(it => isIteratorOverNoDocuments(it))) {
    stats?.mark('intersection-iterator:none')
    return
  }

  // Check if we can optimize the intersection
  if (iterators.length > 1 && iterators.every(it => isIteratorUsingIndexOfDocuments(it) && it.index.isBTree )) {

    yield* _enumerateBTreeIntersection(
      iterators as DocumentMatchIteratorUsingIndex[],
      allDocuments,
      stats
    );
    return;
  }


  const outerIterator = iterators[0]
  const innerIterators = iterators.slice(1)

  for (const document of _enumerateGraphIterator(outerIterator, allDocuments, stats)) {
    let containedInAllOtherIterators = true

    for (const innerIterator of innerIterators) {
      if (isIteratorOverAllDocuments(innerIterator)) {
        continue
      }

      if (isIteratorUsingIndexOfDocuments(innerIterator) ) {
        if (!iteratorContains(innerIterator, document.id)) {
          // We are handling an intersection so if this documentis not in this iterator 
          // it means it is not in the intersection (and we do not need to check the other iterators)
          containedInAllOtherIterators = false
          break
        }
      }

      if (isIteratorUsingJoinsOfIterators(innerIterator)) {
        let foundInIterator = false

        for (const innerDocument of _enumerateGraphIterator(innerIterator, allDocuments, stats)) {
          if (document.id === innerDocument.id) {
            foundInIterator = true
            break
          }
        }

        if (!foundInIterator) {
          containedInAllOtherIterators = false
          break
        }
      }
    }

    if (containedInAllOtherIterators) {
      stats?.increment('enumerated:intersection:set')
      yield document
    }
  }
}

export function* enumerateUnionOfIterators(iterators: (DocumentMatchIteratorJoined|DocumentMatchIteratorUsingIndex|DocumentMatchIteratorUsingId)[], allDocuments: Map<string, Types.Document>, stats?: SingleQueryStats): IterableIterator<Types.Document> {
  if (iterators.length > 1 && iterators.every(it => isIteratorUsingIndexOfDocuments(it) && it.index.isBTree)) {
    yield* _enumerateSortedUnion(
      iterators as DocumentMatchIteratorUsingIndex[],
      allDocuments,
      stats
    )
    return
  }

  for (const iterator of iterators) {
    for (const doc of _enumerateGraphIterator(iterator, allDocuments, stats)) {
      stats?.increment('enumerated:union:set')
      yield doc
    }
  }
}

/**
 * Intersect a list of B-tree index iterators and enumerate the common documents.
 *
 * OPTIMIZED VERSION: Uses idIterator.keys(from) to skip directly to target keys
 * instead of iterating through all intermediate values.
 *
 * IMPORTANT: This works for BOTH BTree and Set-based indexes because they both
 * implement the DocumentIdIterator interface:
 * - BTree: Uses native keys(from) with O(log n) complexity via BTree.entriesReverse
 * - Set: Uses binary search to find starting position, then iterates from there
 *
 * The abstraction is in the Index class which returns DocumentIdIterator regardless
 * of backing storage (BTree or Set).
 *
 * Preconditions
 *  • iterators.length ≥ 2
 *  • every iterator is { type: IterateOverIndex, idIterator: DocumentIdIterator, … }
 *  • idIterator provides sorted iteration with minKey(), maxKey(), and keys(from)
 */
export function* _enumerateBTreeIntersection (
  iterators : DocumentMatchIteratorUsingIndex[],
  allDocs   : Map<string, Types.Document>,
  stats?    : SingleQueryStats
): IterableIterator<Types.Document> {

  /* ------------------------------------------------------------------ *
   * 1.  Early "no-overlap" check using   [globalLo .. globalHi]         *
   * ------------------------------------------------------------------ */
  let globalLo: string | undefined = undefined
  let globalHi: string | undefined = undefined

  for (const it of iterators) {
    if (it.idIterator.size === 0) return
    
    // Use DocumentIdIterator's minKey/maxKey for range check
    const lo = it.idIterator.minKey()
    const hi = it.idIterator.maxKey()
    
    if (lo === undefined || hi === undefined) return
    
    if (globalLo === undefined || lo > globalLo) globalLo = lo
    if (globalHi === undefined || hi < globalHi) globalHi = hi
  }
  
  // ranges don't overlap
  if (globalLo && globalHi && globalLo > globalHi) {
    stats?.mark('btree-intersect:empty')
    return
  }

  /* ------------------------------------------------------------------ *
   * 2.  Cursor initialisation - start all cursors at globalLo          *
   * ------------------------------------------------------------------ */
  type Cursor = {
    idIterator : DocumentIdIterator,
    iter       : Iterator<string>,
    value      : string | undefined;
  }

  const cursors: Cursor[] = iterators.map(it => {
    // keys(from) works for both BTree and Set - abstraction handled internally
    const iter = it.idIterator.keys(globalLo) as Iterator<string>
    const firstValue = iter.next()
    return { 
      idIterator: it.idIterator,
      iter, 
      value: firstValue.done ? undefined : firstValue.value 
    }
  })

  if (cursors.some(c => c.value === undefined)) return

  const intersectInfo = { k: cursors.length, loop: 0, globalLo, globalHi, skips: 0 }
  stats?.mark('btree-intersect:start', intersectInfo)

  /* ------------------------------------------------------------------ *
   * 3.  Multi-way merge/skip loop - OPTIMIZED with keys(from)          *
   * ------------------------------------------------------------------ */
  while (true) {
    intersectInfo.loop++

    // Find the maximum value among all cursors
    let vmax = cursors[0].value as string
    for (let i = 1; i < cursors.length; ++i) {
      const v = cursors[i].value as string
      if (v > vmax) vmax = v
    }

    // Check if all cursors are aligned at vmax
    const aligned = cursors.every(c => c.value === vmax)
    
    if (aligned) {
      // All iterators point to the same document-id, yield it
      const doc = allDocs.get(vmax)
      if (doc) {
        stats?.increment('enumerated:intersection:btree')
        yield doc
      }
      
      // Advance all cursors to the next value
      for (const c of cursors) {
        const n = c.iter.next()
        c.value = n.done ? undefined : n.value
        if (c.value === undefined) return
      }
    } else {
      // Not aligned - use keys(from) to skip directly to vmax
      for (const c of cursors) {
        if (c.value! < vmax) {
          intersectInfo.skips++
          // Trust the abstraction: keys(vmax) works for both BTree and Set
          c.iter = c.idIterator.keys(vmax) as Iterator<string>
          const n = c.iter.next()
          c.value = n.done ? undefined : n.value
          if (c.value === undefined) return
        }
      }
    }
  }
}

function* _enumerateSortedIntersection(
  iterators: DocumentMatchIterator[],
  allDocuments: Map<string, Types.Document>,
  stats?: SingleQueryStats
): IterableIterator<Types.Document> {
  if (iterators.length < 2) return;
  if (iterators.some(it => isIteratorOverNoDocuments(it))) return;

  // Check if all iterators support sorted ID iteration with seeking
  const allHaveIdIterators = iterators.every(it => isIteratorUsingIndexOfDocuments(it));

  if (allHaveIdIterators) {
    // Use optimized version with keys(from) seeking
    yield* _enumerateSortedIntersectionOptimized(
      iterators as DocumentMatchIteratorUsingIndex[],
      allDocuments,
      stats
    );
    return;
  }

  // Fallback to document-level cursor intersection (original implementation)
  type Cursor = { iter: IterableIterator<Types.Document>; value: Types.Document | undefined };
  const cursors: Cursor[] = iterators.map(it => {
    const iter = _enumerateGraphIterator(it, allDocuments, stats);
    const next = iter.next();
    return { iter, value: next.done ? undefined : next.value };
  });

  if (cursors.some(c => !c.value)) return;

  while (true) {
    // Find maximum ID among cursors
    let vmax = cursors[0].value!.id;
    for (const c of cursors) {
      if (c.value!.id > vmax) vmax = c.value!.id;
    }

    // Advance cursors < vmax
    for (const c of cursors) {
      while (c.value && c.value.id < vmax) {
        const next = c.iter.next();
        c.value = next.done ? undefined : next.value;
      }
      if (!c.value) return;
    }

    // Check if all cursors align
    if (cursors.every(c => c.value!.id === vmax)) {
      stats?.increment('enumerated:intersection:sorted')
      yield cursors[0].value!;
      for (const c of cursors) {
        const next = c.iter.next();
        c.value = next.done ? undefined : next.value;
      }
      if (cursors.some(c => !c.value)) return;
    }
  }
}

/**
 * Optimized sorted intersection using DocumentIdIterator.keys(from) for seeking.
 * The abstraction handles both BTree (native O(log n)) and Set (binary search) internally.
 */
function* _enumerateSortedIntersectionOptimized(
  iterators: DocumentMatchIteratorUsingIndex[],
  allDocuments: Map<string, Types.Document>,
  stats?: SingleQueryStats
): IterableIterator<Types.Document> {
  type Cursor = {
    idIterator: DocumentIdIterator,
    iter: Iterator<string>,
    value: string | undefined
  };

  const cursors: Cursor[] = iterators.map(it => {
    const iter = it.idIterator.keys() as Iterator<string>;
    const firstValue = iter.next();
    return {
      idIterator: it.idIterator,
      iter,
      value: firstValue.done ? undefined : firstValue.value
    };
  });

  if (cursors.some(c => c.value === undefined)) return;

  let skips = 0;

  while (true) {
    // Find maximum value among all cursors
    let vmax = cursors[0].value as string;
    for (const c of cursors) {
      if (c.value! > vmax) vmax = c.value!;
    }

    // Check alignment
    const aligned = cursors.every(c => c.value === vmax);

    if (aligned) {
      // Yield the matching document
      const doc = allDocuments.get(vmax);
      if (doc) {
        stats?.increment('enumerated:intersection:sorted:optimized');
        yield doc;
      }

      // Advance all cursors
      for (const c of cursors) {
        const n = c.iter.next();
        c.value = n.done ? undefined : n.value;
        if (c.value === undefined) {
          stats?.mark('sorted-intersect-optimized:complete', { skips });
          return;
        }
      }
    } else {
      // Trust the abstraction: keys(vmax) works efficiently regardless of backing storage
      for (const c of cursors) {
        if (c.value! < vmax) {
          skips++;
          c.iter = c.idIterator.keys(vmax) as Iterator<string>;
          const n = c.iter.next();
          c.value = n.done ? undefined : n.value;
          if (c.value === undefined) {
            stats?.mark('sorted-intersect-optimized:complete', { skips });
            return;
          }
        }
      }
    }
  }
}
